/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle, useMediaQuery } from 'usehooks-ts';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { CreateInboundShipmentNoteForm } from '@forms/client/CreateInboundShipmentNoteForm';
import {
  useInboundProcess,
  useInboundShipmentNote,
  useDespatchNote,
  useOutboundProcess,
  useAuthHttp,
  useGoodsReceivedNote,
  usePickingNote,
  useOutboundShipmentNote,
  useProofOfDelivery,
  useAuthoriseForPicking,
  useDebouncedQuery,
} from '@hooks';
import { CreateDespatchNoteForm } from '@forms/client/CreateDespatchNoteForm';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useAuth } from '@context';
import { InboundProcess, OutboundProcess } from '@features/supplyChain/types';
import {
  Modal as OldModal,
  ModalContainer,
  ModalHeader,
} from '@components/Modal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { BillingCycle } from '@views/Invoice/invoiceTypes';
import { AssignWarehouseModal } from '@views/SalesOrders/AssignWarehouseModal';
import {
  getInboundStatusColor,
  getOutboundStatusColor,
} from '../utils/statusColors';
import { PageHeader } from '../../../tailwindComponents/PageHeader';
import { Checkbox } from '../../../tailwindComponents/Checkbox';
import { SupplyChainProcessDrawers } from './SupplyChainProcessDrawers';
import { PendingProposalsAlert } from './PendingProposalsAlert';
import { NoticeboardCard, NoticeboardCardColor } from './NoticeboardCard';
import { NoticeboardSwimlane } from './NoticeboardSwimlane';
import {
  businessProfileAtom,
  isInboundSidebarOpenAtom,
  isOutboundSidebarOpenAtom,
  selectedInboundProcessIdAtom,
  selectedOutboundProcessIdAtom,
  notificationsAtom,
  billingCyclesAtom,
  paidBillingCyclesAtom,
  inboundProcessesAtom,
  outboundProcessesAtom,
  selectedWarehouseIdAtom,
  selectedShipperIdAtom,
} from '../../../store/jotai';
import {
  INBOUND_SHIPMENT_TYPE,
  LIST_SUPPLY_CHAIN_PROCESSES_URL,
  OUTBOUND_SHIPMENT_TYPE,
  INVOICE_LIST,
} from '../../../constants';
import { InvoiceSideDrawer } from './InvoiceSideDrawer';
import { Select } from '../../../tailwindComponents/Select/Select';
import {
  getLastItemSort,
  updateLastItemSorts,
} from '../../../utils/pagination';
import {
  despatchLineCount,
  despatchTotalProducts,
} from '../../../utils/despatch';

dayjs.extend(utc);
dayjs.extend(timezone);

type OutboundLaneItem = OutboundProcess;

type OutboundFilter = 'all' | 'drafts' | 'despatches';

const ITEMS_LABEL = 'Items';

export function SharedNoticeboard({ isLsp }: { isLsp: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authHttp = useAuthHttp();

  const isDesktop = useMediaQuery('(min-width: 62em)');
  const isTablet = useMediaQuery('(min-width: 48em)');
  const swimlanePageSize = 25;
  const businessProfile = useAtomValue(businessProfileAtom);
  const [isInboundSidebarOpen, setIsInboundSidebarOpen] = useAtom(
    isInboundSidebarOpenAtom
  );
  const [isOutboundSidebarOpen, setIsOutboundSidebarOpen] = useAtom(
    isOutboundSidebarOpenAtom
  );

  const setSelectedInboundProcessId = useSetAtom(selectedInboundProcessIdAtom);
  const setSelectedOutboundProcessId = useSetAtom(
    selectedOutboundProcessIdAtom
  );
  const [notifications] = useAtom(notificationsAtom);

  const [searchParams, setSearchParams] = useSearchParams();

  const supplyChainProcessId = searchParams.get('supplyChainProcessId');
  const processChildType = searchParams.get('processChildType');
  const noticeboardWarehouseId = useAtomValue(selectedWarehouseIdAtom);
  const noticeboardWarehouseClientId = useAtomValue(selectedShipperIdAtom);

  const [isInitialInboundProcessesFetch, setIsInitialInboundProcessesFetch] =
    useState(true);
  const [isLoadingInboundProcesses, setIsLoadingInboundProcesses] =
    useState(false);
  const [isLoadingInboundProcessesError, setIsLoadingInboundProcessesError] =
    useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [includeArchived, setIncludeArchived] = useState(false);
  const [lastInboundItemSorts, setLastInboundItemSorts] = useState<object[]>(
    []
  );

  const [isInitialOutboundProcessesFetch, setIsInitialOutboundProcessesFetch] =
    useState(true);
  const [isLoadingOutboundProcesses, setIsLoadingOutboundProcesses] =
    useState(false);
  const [isLoadingOutboundProcessesError, setIsLoadingOutboundProcessesError] =
    useState(false);
  const [lastOutboundItemSorts, setLastOutboundItemSorts] = useState<object[]>(
    []
  );

  const [fromDate, setFromDate] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [toDate, setToDate] = useState('');

  const { debouncedQuery: searchQuery, setDebouncedQuery: setSearchQuery } =
    useDebouncedQuery();
  const [dateSort, setDateSort] = useState('Newest');

  const [isInvoicesLoading, setIsInvoicesLoading] = useState(false);
  const [isPaidInvoicesLoading, setIsPaidInvoicesLoading] = useState(false);
  const [billingCycles, setBillingCycles] =
    useAtom<BillingCycle[]>(billingCyclesAtom);
  const [paidBillingCycles, setPaidBillingCycles] = useAtom<BillingCycle[]>(
    paidBillingCyclesAtom
  );
  const [isLoadingInvoicesError, setIsLoadingInvoicesError] = useState(false);
  const [isLoadingPaidInvoicesError, setIsLoadingPaidInvoicesError] =
    useState(false);
  const [hasMoreInvoiceData, setHasMoreInvoiceData] = useState<boolean | null>(
    null
  );
  const [hasMorePaidInvoiceData, setHasMorePaidInvoiceData] = useState<
    boolean | null
  >(null);
  const [lastBillingCycleItemSorts, setLastBillingCycleItemSorts] = useState<
    object[]
  >([]);
  const [lastPaidBillingCycleItemSorts, setLastPaidBillingCycleItemSorts] =
    useState<object[]>([]);

  // Invoice sidebar state
  const [isInvoiceSidebarOpen, setIsInvoiceSidebarOpen] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycle | null>(null);

  useDocumentTitle('FLOX - Noticeboard');

  const {
    isCreateInboundShipmentNoteFormOpen,
    setIsCreateInboundShipmentNoteFormOpen,
    handleSubmitInboundShipmentNote,
  } = useInboundShipmentNote();

  const [inboundProcesses, setInboundProcesses] = useAtom(inboundProcessesAtom);
  const [hasMoreInboundData, setHasMoreInboundData] = useState(true);

  const inboundProcessState = useInboundProcess();
  const {
    setSelectedInboundProcess,
    handleScrollToTopOfInbound,
    setIsViewShipmentNoteDialogOpen,
    setIsViewGoodsReceivedNoteDialogOpen,
  } = inboundProcessState;

  const [outboundProcesses, setOutboundProcesses] = useAtom(
    outboundProcessesAtom
  );
  const [hasMoreOutboundData, setHasMoreOutboundData] = useState(true);
  const [outboundFilter, setOutboundFilter] = useState<OutboundFilter>('all');
  const [selectedDraftProcess, setSelectedDraftProcess] =
    useState<OutboundProcess | null>(null);
  const [isAssignWarehouseOpen, setIsAssignWarehouseOpen] = useState(false);
  const outboundProcessState = useOutboundProcess();
  const {
    setSelectedOutboundProcess,
    handleScrollToTopOfOutbound,
    setIsViewDespatchNoteDialogOpen,
    setIsViewPickingConfirmationDialogOpen,
    setIsViewProofOfDeliveryDialogOpen,
  } = outboundProcessState;

  const {
    isCreateDespatchNoteFormOpen,
    setIsCreateDespatchNoteFormOpen,
    handleSubmitDespatchNote,
  } = useDespatchNote();

  const clearProcesses = () => {
    setSelectedInboundProcess(null);
    setSelectedOutboundProcess(null);
    setSelectedInboundProcessId('');
    setSelectedOutboundProcessId('');
    setIsInboundSidebarOpen(false);
    setIsOutboundSidebarOpen(false);
  };

  // MANAGER:
  const goodsReceivedNoteState = useGoodsReceivedNote();
  const { setIsCreateDraftGoodsReceivedNoteFormOpen } = goodsReceivedNoteState;

  const authoriseForPickingState = useAuthoriseForPicking();

  const pickingNoteState = usePickingNote();

  const outboundShipmentNoteState = useOutboundShipmentNote();

  const proofOfDeliveryState = useProofOfDelivery();
  const { setIsCreateProofOfDeliveryFormOpen } = proofOfDeliveryState;

  // END OF IMPORTS

  const openInboundProcess = (inboundProcess: InboundProcess) => {
    setSelectedInboundProcess(inboundProcess);
    setSelectedInboundProcessId(inboundProcess.id);
    if (processChildType) {
      if (processChildType === 'PickingNoteConfirmed') {
        setIsViewPickingConfirmationDialogOpen(true);
      } else if (processChildType === 'GoodsReceivedNoteFinalised') {
        setIsViewGoodsReceivedNoteDialogOpen(true);
      } else if (processChildType === 'CreateDraftGoodsReceivedNote') {
        setIsCreateDraftGoodsReceivedNoteFormOpen(true);
      }
    } else {
      setIsInboundSidebarOpen(true);
      setIsViewShipmentNoteDialogOpen(true);
    }
  };

  const openOutboundProcess = (outboundProcess: OutboundProcess) => {
    if (outboundProcess.status === 'Draft') {
      setSelectedDraftProcess(outboundProcess);
      setIsAssignWarehouseOpen(true);
      return;
    }
    setSelectedOutboundProcess(outboundProcess);
    setSelectedOutboundProcessId(outboundProcess.id);
    if (processChildType) {
      if (processChildType === 'PickingNoteConfirmed') {
        setIsViewPickingConfirmationDialogOpen(true);
      } else if (processChildType === 'GoodsReceivedNoteFinalised') {
        setIsViewProofOfDeliveryDialogOpen(true);
      } else if (processChildType === 'CreateProofOfDelivery') {
        setIsCreateProofOfDeliveryFormOpen(true);
      }
    } else {
      setIsOutboundSidebarOpen(true);
      setIsViewDespatchNoteDialogOpen(true);
    }
  };

  const findProcessOnTheBoardOrFetchIt = async (processId: string) => {
    const processOnTheBoard = [...inboundProcesses, ...outboundProcesses].find(
      (process) => process.id === processId
    );
    if (processOnTheBoard) return processOnTheBoard;

    try {
      const { data } = await authHttp.post<{
        supplyChainProcesses: (InboundProcess | OutboundProcess)[];
      }>(LIST_SUPPLY_CHAIN_PROCESSES_URL, { supplyChainProcessId: processId });
      return data.supplyChainProcesses?.[0] ?? null;
    } catch {
      return null;
    }
  };

  const forgetTheLinkedProcess = () =>
    setSearchParams(
      (params) => {
        const remainingParams = new URLSearchParams(params);
        remainingParams.delete('supplyChainProcessId');
        remainingParams.delete('processChildType');
        return remainingParams;
      },
      { replace: true }
    );

  useEffect(() => {
    if (!supplyChainProcessId) return undefined;
    let isStillLinked = true;

    const openTheLinkedProcess = async () => {
      const linkedProcess = await findProcessOnTheBoardOrFetchIt(
        supplyChainProcessId
      );
      if (!isStillLinked) return;
      if (linkedProcess) {
        clearProcesses();
        if (linkedProcess.type === INBOUND_SHIPMENT_TYPE) {
          openInboundProcess(linkedProcess);
        } else if (linkedProcess.type === OUTBOUND_SHIPMENT_TYPE) {
          openOutboundProcess(linkedProcess);
        }
      }
      forgetTheLinkedProcess();
    };

    openTheLinkedProcess();
    return () => {
      isStillLinked = false;
    };
  }, [supplyChainProcessId, processChildType]);

  const cardForInboundProcess = (inboundProcess: InboundProcess) => {
    const { shipmentNote, goodsReceivedNote, status, notices } = inboundProcess;

    // Atomic items (e.g. bottles) = Σ skuQuantity × amountOfProductInSku.
    const totalItems = (shipmentNote.skus ?? []).reduce(
      (sum, sku) => sum + sku.skuQuantity * (sku.amountOfProductInSku || 1),
      0
    );
    const showItems = totalItems !== (shipmentNote.totalSkuQuantity || 0);
    // Lead with the SKUs' real unit (e.g. "Cases"); fall back to "Stock units" for mixed/Other.
    const skuUnits = (shipmentNote.skus ?? [])
      .map((sku) => sku.unitOfMeasure)
      .filter(Boolean);
    const unitsLabel =
      skuUnits.length > 0 &&
      skuUnits.every((u) => u === skuUnits[0]) &&
      skuUnits[0].toLowerCase() !== 'other'
        ? `${skuUnits[0]}s`
        : 'Stock units';
    const showSkuUnits = !(showItems && unitsLabel === ITEMS_LABEL);

    return (
      <NoticeboardCard
        key={inboundProcess.id}
        status={status}
        referenceNumber={shipmentNote.shipmentNoteNumber}
        color={getInboundStatusColor(status)}
        hasIssues={(notices?.length ?? 0) > 0}
        fields={[
          { label: 'Supplier', value: shipmentNote.supplier?.name || '-' },
          { label: 'Lines', value: String(shipmentNote.skus?.length || 0) },
          ...(showItems
            ? [
                {
                  label: ITEMS_LABEL,
                  value: (
                    <span className='font-semibold text-gray-900'>
                      {totalItems.toLocaleString()}
                    </span>
                  ),
                },
              ]
            : []),
          ...(showSkuUnits
            ? [
                {
                  label: unitsLabel,
                  value: (shipmentNote.totalSkuQuantity || 0).toLocaleString(),
                },
              ]
            : []),
        ]}
        dateLabel={goodsReceivedNote?.finalisedAt ? 'Received date:' : 'ETA:'}
        dateValue={
          goodsReceivedNote?.finalisedAt
            ? dayjs.utc(goodsReceivedNote.finalisedAt).tz().format('DD/MM/YYYY')
            : dayjs
                .utc(shipmentNote.estimatedTimeOfArrival?.dateTime)
                .tz(shipmentNote.estimatedTimeOfArrival?.timeZone)
                .format('DD/MM/YYYY')
        }
        onClick={() => {
          setSelectedInboundProcess(inboundProcess);
          setSelectedInboundProcessId(inboundProcess.id);
          setIsInboundSidebarOpen(true);
          if (isOutboundSidebarOpen) {
            setIsOutboundSidebarOpen(false);
          }
        }}
      />
    );
  };

  const cardForOutboundProcess = (outboundProcess: OutboundProcess) => {
    const { despatchNote, status, notices } = outboundProcess;

    const skuLines = despatchNote?.skus ?? [];
    const skuLineQuantity = skuLines.reduce(
      (sum, sku) => sum + sku.skuQuantity,
      0
    );
    const totalItems = despatchNote ? despatchTotalProducts(despatchNote) : 0;
    // Lead with the SKUs' real unit (e.g. "Cases"); fall back to "Stock units" for mixed/Other.
    const skuUnits = skuLines.map((sku) => sku.unitOfMeasure).filter(Boolean);
    const unitsLabel =
      skuUnits.length > 0 &&
      skuUnits.every((u) => u === skuUnits[0]) &&
      (skuUnits[0] || '').toLowerCase() !== 'other'
        ? `${skuUnits[0]}s`
        : 'Stock units';
    const hasSkuLinesHoldingSeveralItems = skuLines.some(
      (sku) => (sku.amountOfProductInSku ?? 1) > 1
    );
    const showSkuUnits =
      hasSkuLinesHoldingSeveralItems && unitsLabel !== ITEMS_LABEL;

    return (
      <NoticeboardCard
        key={outboundProcess.id}
        status={status}
        referenceNumber={despatchNote?.despatchNoteNumber || '-'}
        source={outboundProcess.salesOrder?.source}
        color={getOutboundStatusColor(status)}
        hasIssues={(notices?.length ?? 0) > 0}
        fields={[
          {
            label: 'Customer',
            value:
              despatchNote?.customer?.name ?? despatchNote?.customerName ?? '-',
          },
          {
            label: 'Lines',
            value: String(despatchNote ? despatchLineCount(despatchNote) : 0),
          },
          {
            label: ITEMS_LABEL,
            value: (
              <span className='font-semibold text-gray-900'>
                {totalItems.toLocaleString()}
              </span>
            ),
          },
          ...(showSkuUnits
            ? [
                {
                  label: unitsLabel,
                  value: skuLineQuantity.toLocaleString(),
                },
              ]
            : []),
        ]}
        dateLabel='RTA:'
        dateValue={
          despatchNote?.requiredTimeOfArrival?.dateTime
            ? dayjs
                .utc(despatchNote.requiredTimeOfArrival.dateTime)
                .tz(despatchNote.requiredTimeOfArrival.timeZone)
                .format('DD/MM/YYYY')
            : '-'
        }
        onClick={() => {
          setSelectedOutboundProcess(outboundProcess);
          setSelectedOutboundProcessId(outboundProcess.id);
          setIsOutboundSidebarOpen(true);
          if (isInboundSidebarOpen) {
            setIsInboundSidebarOpen(false);
          }
        }}
      />
    );
  };

  const cardForDraftProcess = (process: OutboundProcess) => {
    const order = process.salesOrder;
    const lines = order?.lines ?? [];
    const totalItems = lines.reduce((sum, line) => sum + line.quantity, 0);
    return (
      <NoticeboardCard
        key={`draft-${process.id}`}
        status='Draft'
        referenceNumber={order?.orderNumber || order?.externalOrderId || '-'}
        source={order?.source}
        color='gray'
        fields={[
          { label: 'Customer', value: order?.customerName || '-' },
          { label: 'Lines', value: String(lines.length) },
          { label: 'Items', value: totalItems.toLocaleString() },
        ]}
        dateLabel='Ordered:'
        dateValue={
          order?.orderPlacedAt
            ? dayjs(order.orderPlacedAt).format('DD/MM/YYYY')
            : '-'
        }
        onClick={() => {
          setSelectedDraftProcess(process);
          setIsAssignWarehouseOpen(true);
        }}
      />
    );
  };

  const draftLaneItems: OutboundLaneItem[] = isLsp
    ? []
    : outboundProcesses.filter((process) => process.status === 'Draft');
  const processLaneItems: OutboundLaneItem[] = outboundProcesses.filter(
    (process) => process.status !== 'Draft' && process.status !== 'Ignored'
  );
  const outboundLaneItems: OutboundLaneItem[] =
    outboundFilter === 'despatches'
      ? processLaneItems
      : outboundFilter === 'drafts'
      ? draftLaneItems
      : [...draftLaneItems, ...processLaneItems];

  const renderOutboundLaneItem = (item: OutboundLaneItem) =>
    item.status === 'Draft'
      ? cardForDraftProcess(item)
      : cardForOutboundProcess(item);

  const showOutboundFilter =
    !isLsp && (draftLaneItems.length > 0 || outboundFilter !== 'all');
  const outboundFilterControl = showOutboundFilter ? (
    <select
      value={outboundFilter}
      onChange={(event) =>
        setOutboundFilter(event.target.value as OutboundFilter)
      }
      className='rounded-md border border-gray-200 bg-white px-1.5 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-200'
      title='Filter outbound cards'
    >
      <option value='all'>All</option>
      <option value='drafts'>Drafts ({draftLaneItems.length})</option>
      <option value='despatches'>Despatches</option>
    </select>
  ) : undefined;

  const cardForTransaction = (billingCycle: BillingCycle) => {
    const invoice = isLsp
      ? billingCycle.lspToFloxInvoice
      : billingCycle.floxToShipperInvoice;
    const status = invoice?.status ?? 'Pending';
    const getColor = (): NoticeboardCardColor => {
      if (status === 'Paid') return 'green';
      if (status === 'Approved' || status === 'Finalised') return 'blue';
      return 'yellow';
    };

    const total =
      invoice?.lines?.reduce((sum, line) => sum + line.amount, 0) ?? 0;
    const currencySymbol = invoice?.lines?.[0]?.currencySymbol ?? '£';

    return (
      <NoticeboardCard
        key={billingCycle.id}
        status={status}
        referenceNumber={invoice?.invoiceNumber ?? '-'}
        color={getColor()}
        fields={[
          {
            label: isLsp ? 'Shipper' : 'Warehouse',
            value: isLsp
              ? billingCycle.shipperBusinessName || '-'
              : billingCycle.lspBusinessName || '-',
          },
          {
            label: 'Items',
            value: String(invoice?.lines?.length || 0),
          },
          {
            label: 'Total',
            value: `${currencySymbol}${total.toFixed(2)}`,
          },
        ]}
        dateLabel='Due:'
        dateValue={
          invoice?.dueDate ? dayjs(invoice.dueDate).format('DD/MM/YYYY') : '-'
        }
        onClick={() => {
          setSelectedBillingCycle(billingCycle);
          setIsInvoiceSidebarOpen(true);
          // Close other sidebars
          if (isInboundSidebarOpen) setIsInboundSidebarOpen(false);
          if (isOutboundSidebarOpen) setIsOutboundSidebarOpen(false);
        }}
      />
    );
  };

  const cardForInvoice = (billingCycle: BillingCycle) => {
    const invoice = isLsp
      ? billingCycle.lspToFloxInvoice
      : billingCycle.floxToShipperInvoice;
    const status = invoice?.status ?? 'Paid';
    const getColor = (): NoticeboardCardColor => {
      if (status === 'Paid') return 'green';
      return 'blue';
    };

    const total =
      invoice?.lines?.reduce((sum, line) => sum + line.amount, 0) ?? 0;
    const currencySymbol = invoice?.lines?.[0]?.currencySymbol ?? '£';

    return (
      <NoticeboardCard
        key={billingCycle.id}
        status={status}
        referenceNumber={invoice?.invoiceNumber ?? '-'}
        color={getColor()}
        fields={[
          {
            label: isLsp ? 'Shipper' : 'Warehouse',
            value: isLsp
              ? billingCycle.shipperBusinessName || '-'
              : billingCycle.lspBusinessName || '-',
          },
          {
            label: 'Items',
            value: String(invoice?.lines?.length || 0),
          },
          {
            label: 'Total',
            value: `${currencySymbol}${total.toFixed(2)}`,
          },
        ]}
        dateLabel='Paid:'
        dateValue={
          invoice?.paidAt ? dayjs(invoice.paidAt).format('DD/MM/YYYY') : '-'
        }
        onClick={() => {
          setSelectedBillingCycle(billingCycle);
          setIsInvoiceSidebarOpen(true);
          // Close other sidebars
          if (isInboundSidebarOpen) setIsInboundSidebarOpen(false);
          if (isOutboundSidebarOpen) setIsOutboundSidebarOpen(false);
        }}
      />
    );
  };

  const fetchInboundSupplyChainProcesses = async (
    isGoingNext: boolean | undefined = undefined
  ) => {
    if (
      isInitialInboundProcessesFetch ||
      isLoadingInboundProcesses ||
      isLoadingInboundProcessesError
    ) {
      setIsInitialInboundProcessesFetch(false);
      return;
    }

    setIsLoadingInboundProcesses(true);

    try {
      const lastItemSort = getLastItemSort(isGoingNext, lastInboundItemSorts);

      const { data } = await authHttp.post(LIST_SUPPLY_CHAIN_PROCESSES_URL, {
        searchQuery,
        warehouseId:
          noticeboardWarehouseId === 'All' ? null : noticeboardWarehouseId,
        shipperBusinessId: isLsp
          ? noticeboardWarehouseClientId === 'All'
            ? null
            : noticeboardWarehouseClientId
          : null,
        type: INBOUND_SHIPMENT_TYPE,
        orderBy: 'CreatedAt',
        isOrderAscending: dateSort !== 'Newest',
        pagination: {
          lastItemSort,
          itemsPerPage: swimlanePageSize,
        },
        archivedByShipperStatus: isLsp
          ? null
          : includeArchived
          ? null
          : 'NotArchived',
        archivedByLspStatus: !isLsp
          ? null
          : includeArchived
          ? null
          : 'NotArchived',
        grnFinalisationFromDate: fromDate,
        grnFinalisationToDate: toDate,
      });

      updateLastItemSorts(
        isGoingNext,
        setLastInboundItemSorts,
        data?.lastItemSort
      );
      setHasMoreInboundData(
        !(data.supplyChainProcesses.length < swimlanePageSize)
      );
      const isFirstInboundPage = lastItemSort === null;
      if (isFirstInboundPage) {
        setInboundProcesses(data.supplyChainProcesses);
      } else {
        setInboundProcesses((prev) => {
          const existingIds = new Set(prev.map((process) => process.id));
          return [
            ...prev,
            ...data.supplyChainProcesses.filter(
              (process: InboundProcess) => !existingIds.has(process.id)
            ),
          ];
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
      setIsLoadingInboundProcessesError(true);
    } finally {
      setIsLoadingInboundProcesses(false);
    }
  };

  const fetchOutboundSupplyChainProcesses = async (
    isGoingNext: boolean | undefined = undefined
  ) => {
    if (
      isInitialOutboundProcessesFetch ||
      isLoadingOutboundProcesses ||
      isLoadingOutboundProcessesError
    ) {
      setIsInitialOutboundProcessesFetch(false);
      return;
    }

    setIsLoadingOutboundProcesses(true);

    try {
      const lastItemSort = getLastItemSort(isGoingNext, lastOutboundItemSorts);
      const { data } = await authHttp.post(LIST_SUPPLY_CHAIN_PROCESSES_URL, {
        searchQuery,
        warehouseId:
          noticeboardWarehouseId === 'All' ? null : noticeboardWarehouseId,
        shipperBusinessId: isLsp
          ? noticeboardWarehouseClientId === 'All'
            ? null
            : noticeboardWarehouseClientId
          : null,
        type: OUTBOUND_SHIPMENT_TYPE,
        orderBy: 'CreatedAt',
        isOrderAscending: dateSort !== 'Newest',
        pagination: {
          lastItemSort,
          itemsPerPage: swimlanePageSize,
        },
        archivedByShipperStatus: isLsp
          ? null
          : includeArchived
          ? null
          : 'NotArchived',
        archivedByLspStatus: !isLsp
          ? null
          : includeArchived
          ? null
          : 'NotArchived',
        pickingNoteConfirmationFromDate: fromDate,
        pickingNoteConfirmationToDate: toDate,
      });

      updateLastItemSorts(
        isGoingNext,
        setLastOutboundItemSorts,
        data?.lastItemSort
      );
      setHasMoreOutboundData(
        !(data.supplyChainProcesses.length < swimlanePageSize)
      );
      const isFirstOutboundPage = lastItemSort === null;
      if (isFirstOutboundPage) {
        setOutboundProcesses(data.supplyChainProcesses);
      } else {
        setOutboundProcesses((prev) => {
          const existingIds = new Set(prev.map((process) => process.id));
          return [
            ...prev,
            ...data.supplyChainProcesses.filter(
              (process: OutboundProcess) => !existingIds.has(process.id)
            ),
          ];
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error: ', error);
      setIsLoadingOutboundProcessesError(true);
    } finally {
      setIsLoadingOutboundProcesses(false);
    }
  };


  const fetchInvoices = async (
    isGoingNext: boolean | undefined = undefined
  ) => {
    if (!user?.roles.includes('FinanceAdmin') || isInvoicesLoading) return;
    setIsInvoicesLoading(true);
    try {
      const lastItemSort = getLastItemSort(
        isGoingNext,
        lastBillingCycleItemSorts
      );
      const { data } = await authHttp.post(
        INVOICE_LIST,
        {
          statuses: !isLsp ? ['Finalised', 'Paid'] : '',
          warehouseId:
            noticeboardWarehouseId === 'All' ? null : noticeboardWarehouseId,
          shipperBusinessId: isLsp
            ? noticeboardWarehouseClientId === 'All'
              ? null
              : noticeboardWarehouseClientId
            : null,
          searchQuery,
          createdBetween: {
            startDate: fromDate,
            endDate: toDate,
          },
          orderBy: 'CreatedAt',
          isOrderAscending: dateSort !== 'Newest',
          pagination: {
            lastItemSort,
            itemsPerPage: swimlanePageSize,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data) {
        updateLastItemSorts(
          isGoingNext,
          setLastBillingCycleItemSorts,
          data.lastItemSort
        );
        setHasMoreInvoiceData(!(data.billingCycles.length < swimlanePageSize));
        if (isGoingNext === undefined) {
          setBillingCycles(data.billingCycles);
        } else {
          setBillingCycles([...billingCycles, ...data.billingCycles]);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setIsLoadingInvoicesError(true);
      setHasMoreInvoiceData(false);
    } finally {
      setIsInvoicesLoading(false);
    }
  };

  const fetchPaidInvoices = async (
    isGoingNext: boolean | undefined = undefined
  ) => {
    if (!user?.roles.includes('FinanceAdmin') || isPaidInvoicesLoading) return;
    setIsPaidInvoicesLoading(true);
    try {
      const lastItemSort = getLastItemSort(
        isGoingNext,
        lastPaidBillingCycleItemSorts
      );
      const { data } = await authHttp.post(
        INVOICE_LIST,
        {
          statuses: ['Paid'],
          warehouseId:
            noticeboardWarehouseId === 'All' ? null : noticeboardWarehouseId,
          shipperBusinessId: isLsp
            ? noticeboardWarehouseClientId === 'All'
              ? null
              : noticeboardWarehouseClientId
            : null,
          orderBy: 'CreatedAt',
          isOrderAscending: dateSort !== 'Newest',
          searchQuery,
          createdBetween: {
            startDate: fromDate,
            endDate: toDate,
          },
          pagination: {
            lastItemSort,
            itemsPerPage: swimlanePageSize,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data) {
        updateLastItemSorts(
          isGoingNext,
          setLastPaidBillingCycleItemSorts,
          data.lastItemSort
        );
        setHasMorePaidInvoiceData(
          !(data.billingCycles.length < swimlanePageSize)
        );
        if (isGoingNext === undefined) {
          setPaidBillingCycles(data.billingCycles);
        } else {
          setPaidBillingCycles([...paidBillingCycles, ...data.billingCycles]);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setIsLoadingPaidInvoicesError(true);
      setHasMorePaidInvoiceData(false);
    } finally {
      setIsPaidInvoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchInboundSupplyChainProcesses();
    fetchOutboundSupplyChainProcesses();
    fetchInvoices();
    fetchPaidInvoices();
  }, [
    fromDate,
    toDate,
    searchQuery,
    dateSort,
    noticeboardWarehouseId,
    noticeboardWarehouseClientId,
  ]);

  useEffect(() => {
    fetchInboundSupplyChainProcesses();
    fetchOutboundSupplyChainProcesses();
  }, [includeArchived]);

  useEffect(() => {
    const notification = notifications?.[0];
    if (
      notification &&
      notification.notificationType === 'BillingCycle.InvoiceApproved'
    ) {
      fetchInvoices(true);
    }

    if (
      notification &&
      notification.notificationType === 'BillingCycle.PaymentMade'
    ) {
      fetchPaidInvoices(true);
    }
  }, [notifications]);

  return (
    <div className='flex flex-col h-screen bg-white'>
      {/* Page Header */}
      <PageHeader
        header='Noticeboard'
        pages={[{ name: 'Noticeboard', href: '/noticeboard', current: true }]}
      />

      {isLsp && <PendingProposalsAlert />}

      {/* Filter Bar */}
      <div className='bg-white border-b border-gray-200 px-6 py-3'>
        <div className='flex flex-wrap items-center justify-between gap-4'>
          {/* Left side: Date picker, Search, Filters */}
          <div className='flex items-center gap-3'>
            {/* Date Range Picker */}
            <div className='flex items-center gap-2'>
              <input
                type='date'
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className='px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200'
                placeholder='Select date(s)'
              />
            </div>

            {/* Search */}
            <div className='relative'>
              <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
              <input
                type='text'
                placeholder='Search...'
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 w-48'
              />
            </div>

            {/* Filters Button */}
            {/* <button
              type='button'
              className='flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50'
            >
              <FunnelIcon className='h-4 w-4' />
              Filters
            </button> */}

            {/* Action Required Only Checkbox */}
            {/* <Checkbox
              label='Action required only'
              checked={false}
              onChange={() => {}}
            /> */}
          </div>

          {/* Right side: Sort controls */}
          <div className='flex items-center gap-4'>
            {/* Names Sort */}
            {/* <Select
              label='Names:'
              options={[
                { label: 'A - Z', value: 'A - Z' },
                { label: 'Z - A', value: 'Z - A' },
              ]}
              orientation='horizontal'
              selected={{ label: 'A - Z', value: 'A - Z' }}
              setSelected={() => {}}
              sizeVariant='md'
            /> */}

            {/* Date Sort */}
            <Select
              label='Date:'
              options={[
                { label: 'Newest', value: 'Newest' },
                { label: 'Oldest', value: 'Oldest' },
              ]}
              orientation='compact'
              selected={{ label: dateSort, value: dateSort }}
              setSelected={(option) => setDateSort(option.value as string)}
              sizeVariant='md'
            />
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className='flex-1 overflow-x-auto overflow-y-hidden p-3 sm:p-6 bg-gray-50'>
        <div className='flex gap-3 sm:gap-4 h-full'>
          {/* Inbound */}
          <NoticeboardSwimlane<InboundProcess>
            title='Inbound'
            name='inbound'
            itemCount={inboundProcesses.length}
            items={inboundProcesses}
            hasMoreData={hasMoreInboundData}
            isLoadingError={isLoadingInboundProcessesError}
            loadMore={() => fetchInboundSupplyChainProcesses(true)}
            renderItem={cardForInboundProcess}
            handleScrollToTop={handleScrollToTopOfInbound}
            showAddButton={!isLsp}
            onAddClick={() => navigate('/inbound-shipment/create')}
          />

          {/* Outbound */}
          <NoticeboardSwimlane<OutboundLaneItem>
            title='Outbound'
            name='outbound'
            itemCount={outboundLaneItems.length}
            items={outboundLaneItems}
            hasMoreData={hasMoreOutboundData}
            isLoadingError={isLoadingOutboundProcessesError}
            loadMore={() => fetchOutboundSupplyChainProcesses(true)}
            renderItem={renderOutboundLaneItem}
            handleScrollToTop={handleScrollToTopOfOutbound}
            headerContent={outboundFilterControl}
            showAddButton={!isLsp}
            addButtonDisabled={
              !(
                businessProfile?.hasConnectedBankAccount === true ||
                businessProfile?.areFinancialFeaturesDisabled === true
              )
            }
            onAddClick={() => navigate('/despatch-note/create')}
          />

          {/* Transactions */}
          {user?.roles.includes('FinanceAdmin') && (
            <NoticeboardSwimlane<BillingCycle>
              title='Transactions'
              name='transactions'
              itemCount={billingCycles.length}
              items={billingCycles}
              hasMoreData={hasMoreInvoiceData}
              isLoadingError={isLoadingInvoicesError}
              loadMore={() => fetchInvoices(true)}
              renderItem={cardForTransaction}
              handleScrollToTop={() =>
                document.getElementById('transactions-list')?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }
            />
          )}

          {/* Invoices */}
          {user?.roles.includes('FinanceAdmin') && (
            <NoticeboardSwimlane<BillingCycle>
              title='Invoices'
              name='invoices'
              itemCount={paidBillingCycles.length}
              items={paidBillingCycles}
              hasMoreData={hasMorePaidInvoiceData}
              isLoadingError={isLoadingPaidInvoicesError}
              loadMore={() => fetchPaidInvoices(true)}
              renderItem={cardForInvoice}
              handleScrollToTop={() =>
                document.getElementById('invoices-list')?.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                })
              }
            />
          )}
        </div>
      </div>

      {/* TODO: Doesn't look like widgetRoot is being used anywhere. Can we remove this? */}
      <div id='widgetRoot' />

      <SupplyChainProcessDrawers
        isLsp={isLsp}
        isDesktop={isDesktop}
        isTablet={isTablet}
        inboundProcess={inboundProcessState}
        outboundProcess={outboundProcessState}
        goodsReceivedNote={goodsReceivedNoteState}
        authoriseForPicking={authoriseForPickingState}
        pickingNote={pickingNoteState}
        outboundShipmentNote={outboundShipmentNoteState}
        proofOfDelivery={proofOfDeliveryState}
      />

      {/* Invoice Sidebar */}
      <InvoiceSideDrawer
        selectedBillingCycle={selectedBillingCycle}
        setSelectedBillingCycle={setSelectedBillingCycle}
        isOpen={isInvoiceSidebarOpen}
        setIsOpen={setIsInvoiceSidebarOpen}
        isDesktop={isDesktop}
        isLsp={isLsp}
      />

      {/* Shipper-only forms: */}
      {/* Create inbound shipment note form */}
      <OldModal
        open={isCreateInboundShipmentNoteFormOpen}
        onOpenChange={(isOpen: boolean) =>
          setIsCreateInboundShipmentNoteFormOpen(isOpen)
        }
      >
        <ModalContainer preventCloseOnOutsideClick>
          <ModalHeader title='Create Inbound Shipment Note' />
          <CreateInboundShipmentNoteForm
            onClose={() => {
              setIsCreateInboundShipmentNoteFormOpen(false);
            }}
            onSubmit={handleSubmitInboundShipmentNote}
          />
        </ModalContainer>
      </OldModal>
      {/* Create despatch note */}
      <OldModal
        open={isCreateDespatchNoteFormOpen}
        onOpenChange={(isOpen: boolean) =>
          setIsCreateDespatchNoteFormOpen(isOpen)
        }
      >
        <ModalContainer preventCloseOnOutsideClick>
          <ModalHeader title='Create Despatch Note' />
          <CreateDespatchNoteForm
            onClose={() => {
              setIsCreateDespatchNoteFormOpen(false);
            }}
            onSubmit={handleSubmitDespatchNote}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </ModalContainer>
      </OldModal>
      <AssignWarehouseModal
        draft={selectedDraftProcess}
        isOpen={isAssignWarehouseOpen}
        setIsOpen={setIsAssignWarehouseOpen}
        onActioned={() => {
          fetchOutboundSupplyChainProcesses();
        }}
      />

    </div>
  );
}
